'use strict';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/sampleTime';
const angular = require('angular');
const {extend, forEach, fromJson, isDefined, isString, isUndefined, toJson} = angular;

//This service is mostly a clone of the AngularJS $cacheFactory, with additional support for persisting the cache in localStorage
//Lawnchair or similar could conceivably be substituted for direct WebStorage API access to utilize IndexedDB or WebSQL instead.
//RxJS is used to limit writes to the file system to minimize the performance impact of doing writes to cache in loops.
angular.module('persistent-cache', [])
  .provider('$persistentCacheFactory', PersistentCacheFactoryProvider);

var STORAGE_CACHE_ID = '$PersistentCacheFactory_CACHE';
var THROTTLE_PERIOD = 500;

// TODO: track persisted cache ids so they can be destroyed with destroyAll
function PersistentCacheFactoryProvider() {
  this.$get = [
    '$window',
    '$log',
    '$exceptionHandler',
    '$cacheFactory',
    PersistentCacheFactory];
}

function PersistentCacheFactory(
  $window,
  $log,
  $exceptionHandler,
  $cacheFactory,
) {
  var caches = {};
  var supportsLocalStorage = isDefined($window.localStorage);

  function persistentCacheFactory(cacheId, options) {
    options = options || {};

    // Default to the angular cache factory if localstorage is not available or requested
    if (options.useLocalStorage === false || !supportsLocalStorage) return $cacheFactory(cacheId);

    if (cacheId in caches) {
      throw new Error('$persistentCacheFactory: CacheId ' + cacheId + ' is already taken!');
    }

    // TODO: determine a reasonable default capacity basic on the limits of the Web Storage specification
    var size,
      stats = extend({}, options, {id: cacheId}),
      data,
      capacity = (options && options.capacity) || 150, //Number.MAX_VALUE,
      lruHash = {},
      freshEnd = null,
      staleEnd = null,
      storageId = STORAGE_CACHE_ID + cacheId,
      storageSubject = new Subject(),
      storageSubscription = storageSubject
        .sampleTime(THROTTLE_PERIOD)
        .subscribe(writeBufferToStorage, handleError, onComplete);

    // Load this cache from local storage if it exists. This is currently synchronous
    if ($window.localStorage) {
      data = window.localStorage.getItem(storageId);
      data = data ? fromJson(data) : {};
      size = Object.keys(data).length;

      try {
        var lruEntry;
        for (var key in data) {
          if (data.hasOwnProperty(key)) {
            lruEntry = lruHash[key] = {key: key};
            refresh(lruEntry);
            size++;
          }
        }
      } catch(e) {
        console.warn('$persistentCacheFactory init failed!');
        console.error(e)
      }
    }

    return caches[cacheId] = {

      put: function (key, value) {
        if (!isString(key) || isUndefined(value)) return;
        var lruEntry = lruHash[key] || (lruHash[key] = {key: key});

        refresh(lruEntry);

        if (!(key in data)) size++;
        data[key] = value;

        if (size > capacity) {
          // remove will trigger a write
          this.remove(staleEnd.key);
        } else if (supportsLocalStorage) {
          storageSubject.next({key: key, value: value});
        }

        return value;
      },

      get: function (key) {
        var lruEntry = lruHash[key];

        if (!lruEntry) return;

        refresh(lruEntry);

        return data[key];
      },

      // Do not mutate data. Only for more performant initialization usage 
      getAll: function () {
        return data;
      },

      remove: function (key) {
        _removeInternal(key);
        supportsLocalStorage && storageSubject.next({key: key, value: null});
      },

      removeAll: function () {
        data = {};
        size = 0;
        lruHash = {};
        freshEnd = staleEnd = null;
        supportsLocalStorage && storageSubject.next(null);
      },

      destroy: function () {
        data = null;
        stats = null;
        lruHash = null;
        delete caches[cacheId];
        //TODO: determine away to insert this command into the stream

        storageSubject.unsubscribe();
        storageSubscription.unsubscribe();
        try {
          supportsLocalStorage && $window.localStorage.removeItem(storageId);
        } catch (e) {
          if (e.name === "QUOTA_EXCEEDED_ERR" || e.name === "NS_ERROR_DOM_QUOTA_REACHED") {
            // removing data really shouldn't throw this error. Need to investigate
          }
          $log.warn("LocalStorage Exception ==> " + e.message);
        }
      },

      flush: writeBufferToStorage,

      info: function () {
        return extend({}, stats, {size: size});
      },

      size: function () {
        return size;
      }
    };

    
    /**
     * makes the `entry` the freshEnd of the LRU linked list
     */
    function refresh(entry) {
      if (entry != freshEnd) {
        if (!staleEnd) {
          staleEnd = entry;
        } else if (staleEnd == entry) {
          staleEnd = entry.n;
        }

        link(entry.n, entry.p);
        link(entry, freshEnd);
        freshEnd = entry;
        freshEnd.n = null;
      }
    }

    /**
     * bidirectionally links two entries of the LRU linked list
     */
    function link(nextEntry, prevEntry) {
      if (nextEntry != prevEntry) {
        if (nextEntry) nextEntry.p = prevEntry; //p stands for previous, 'prev' didn't minify
        if (prevEntry) prevEntry.n = nextEntry; //n stands for next, 'next' didn't minify
      }
    }

    // Web Storage handlers

    function _removeInternal(key) {
      var lruEntry = lruHash[key];

      if (!lruEntry) return;

      if (lruEntry == freshEnd) freshEnd = lruEntry.p;
      if (lruEntry == staleEnd) staleEnd = lruEntry.n;
      link(lruEntry.n, lruEntry.p);

      delete lruHash[key];

      delete data[key];
      size--;
    }

    function writeBufferToStorage() {
      if (size === null) {
        // do nothing
      } else if (size === 0) {
        try {
          $window.localStorage.removeItem(storageId);
        } catch (e) {
          if (e.name === "QUOTA_EXCEEDED_ERR" || e.name === "NS_ERROR_DOM_QUOTA_REACHED") {
            // removing data really shouldn't throw this error. Need to investigate
          }
          $log.warn("LocalStorage Exception ==> " + e.message);
        }
      } else {
        try {
          $window.localStorage.setItem(storageId, toJson(data));
        } catch (e) {
          _reduceStorage();
        }
      }
    }

    function _reduceStorage() {
      var exceedsStorage = true;
      var maxRetryAttempts = 10;
      while (exceedsStorage && maxRetryAttempts > 0) {
        maxRetryAttempts--;

        if (maxRetryAttempts == 0) {
          // may want to try _reduceStorage on other persistentCache instances first?
          destroyAll(); // some other cache is causing a problem probably. Kill the local cache and start over
          return;
        }

        try {
          _removeInternal(staleEnd.key);
          $window.localStorage.setItem(storageId, toJson(data));
          exceedsStorage = false;
        } catch (e) {
          if (e.name === "QUOTA_EXCEEDED_ERR" || e.name === "NS_ERROR_DOM_QUOTA_REACHED") {
            exceedsStorage = true;
          }
          $log.warn("LocalStorage Exception ==> " + e.message);
        }
      }
    }

    function handleError(error) {
      $exceptionHandler(error);
    }

    function onComplete() {

    }
  }

  persistentCacheFactory.info = info;

  persistentCacheFactory.get = getItem;

  // Needed for unit tests
  persistentCacheFactory.destroyAll = destroyAll;

  function info() {
    var info = {};
    forEach(caches, function (cache, cacheId) {
      info[cacheId] = cache.info();
    });
    return info;
  }

  function getItem(cacheId) {
    var cache = caches[cacheId];
    return cache || $cacheFactory.get(cacheId);
  }

  function destroyAll() {
    caches = {};

    if (supportsLocalStorage) {
      for ( var i = 0, len = localStorage.length; i < len; ++i ) {
        var key = localStorage.key(i);
        if (isString(key) && key.indexOf(STORAGE_CACHE_ID) > -1) {
          localStorage.removeItem(key);
        }
      }
    }
  }


  return persistentCacheFactory;
}


// WEBPACK FOOTER //
// ./src/js/_libs/persistent-cache/PersistentCacheFactory.js